import React, { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import {
  Button,
  CustomDropdown,
  DropdownOption,
  PencilIcon,
  TrashIcon,
  dropdownTriggerClasses,
  useToast,
} from "../../../components/ui";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
// The same glyph the Dishes rail item wears, and deliberately: the mark in
// this list means "this came from your Dishes screen", so it has to be the
// thing that screen is known by rather than a second icon for one idea.
import { ReactComponent as DishMark } from "../../../assets/svg/sidebar/dishes.svg";
import {
  useFoodNutrition,
  useFoodSearch,
  useFoodUnits,
} from "../hooks/useFoodCatalog";
import {
  createDishEntry,
  dishMacros,
  formatMacro,
  isDishComplete,
  isNumberDraft,
  unitLabel,
  unitOptionLabel,
} from "../planMapping";
import CustomDishModal from "./CustomDishModal";
import OptionActions from "./OptionActions";
import {
  MACRO_COLUMNS,
  MacroHeading,
  OPTION_COLUMN_COUNT,
  OptionColgroup,
  optionCardClasses,
  optionCell,
  optionHeadCell,
  optionInputClasses,
  optionRuleClasses,
  optionTableClasses,
  optionTextCell,
} from "./optionTable";
import type { DishEntry, MealOption } from "../types/plans.types";

/**
 * The one cell of a row that is typed rather than picked: how much of the unit
 * is eaten.
 *
 * What is typed is held as text for as long as the box has focus, because the
 * text is ahead of the number on the way in — `1.` is a step towards `1.5`,
 * and a box that showed back the `1` the row had stored would eat the point as
 * it was typed. The row is told the number behind it on every keystroke all
 * the same, so the totals keep up.
 */
const NumberInput: React.FC<{
  value: number;
  onChange: (value: number) => void;
  placeholder: string;
  /** What the box is called where the column heading cannot be read. */
  label: string;
}> = ({ value, onChange, placeholder, label }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? (value ? String(value) : "");

  return (
    <input
      type="text"
      // Text rather than `number`, which brings spinners and a wheel that
      // changes the value under a scrolling page. `decimal` still asks a phone
      // for the numeric keypad.
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      aria-label={label}
      onChange={(event) => {
        const next = event.target.value;
        // Anything else is simply not taken: the box keeps what it had rather
        // than flashing a value it is about to reject.
        if (!isNumberDraft(next)) return;
        setDraft(next);
        onChange(Number(next) || 0);
      }}
      // Back to what the row holds, so a half-typed `1.` settles as `1`.
      onBlur={() => setDraft(null)}
      // Narrower than its column: a quantity is two characters, and a box the
      // width of the dish name would say otherwise.
      className={`${optionInputClasses} max-w-[150px]`}
    />
  );
};

interface CatalogDishCellsProps {
  dish: DishEntry;
  onUpdate: (patch: Partial<DishEntry>) => void;
  /**
   * Handing the row over to the custom dish dialog, with whatever had been
   * typed into the Dish field when its list let the search down.
   */
  onWriteCustom: (typed: string) => void;
}

/**
 * A row picked off the food catalog: the dish and its unit come from the API,
 * and the macros are whatever it priced them at, so those four columns are
 * read rather than written.
 *
 * The way out is the "Custom dish" row pinned under the dish list, which opens
 * `CustomDishModal` — see `CustomDishCells` for what the row becomes once it
 * has been written there.
 */
const CatalogDishCells: React.FC<CatalogDishCellsProps> = ({
  dish,
  onUpdate,
  onWriteCustom,
}) => {
  const toast = useToast();

  const foodSearch = useFoodSearch();
  const units = useFoodUnits(dish.foodId ?? null);
  useFoodNutrition(dish.foodId ?? null, dish.unitId ?? null, dish.quantity, (result) =>
    onUpdate(result ? { macros: result.macros, unitLabel: result.unitLabel } : { macros: null })
  );

  // The search results plus whatever the row already holds — a query typed
  // after picking a food would otherwise drop it from the list the trigger
  // reads its own label off, and the box would go blank despite the row still
  // holding a value.
  const dishOptions: DropdownOption[] = useMemo(() => {
    const results = foodSearch.options.map((food) => ({
      value: String(food.id),
      // The wording the search hit, which is the one that explains why the row
      // is on the list at all — "Pudina chatni" for a query of "pudina". The
      // food's own name goes underneath when the two differ, so picking an
      // alias is never picking something unnamed.
      label: food.matchedName,
      subtitle: food.matchedName === food.name ? undefined : food.name,
      // Marked, not sorted into a section: the workspace's own dishes come
      // back interleaved with the catalog's by how well each matched what was
      // typed, and that ranking is the useful one — "Ragi Dosa" of yours above
      // "Ragi dosa (homemade)" is the search working. Grouping them would
      // throw that away to answer a question the reader is not asking yet.
      badge: food.isOwn ? (
        <>
          {/* The glyph carries no words, so the row lends it some — without
              this the option reads out as its name alone and the mark is
              invisible to anyone not looking at it. */}
          <span className="sr-only">Your dish</span>
          <DishMark aria-hidden="true" className="h-4 w-4" />
        </>
      ) : undefined,
    }));
    if (
      dish.foodId != null &&
      !results.some((option) => option.value === String(dish.foodId))
    ) {
      // No mark on this one: it is the row's own saved value put back on the
      // list so the box can read its label, and nothing on a `DishEntry` says
      // which catalog it came out of. Marking it either way would be a guess.
      results.unshift({
        value: String(dish.foodId),
        label: dish.dish,
        subtitle: undefined,
        badge: undefined,
      });
    }
    return results;
  }, [foodSearch.options, dish.foodId, dish.dish]);

  // Named with the weight behind them — "Cup (small) - 100gm" — since three
  // cups are only a choice once there is something to tell them apart by.
  const unitOptions: DropdownOption[] = useMemo(
    () =>
      units.options.map((unit) => ({
        value: String(unit.id),
        label: unitOptionLabel(unit),
      })),
    [units.options]
  );

  // A row from before the catalog endpoints landed (a seeded or hand-built
  // plan) has a name but no id — shown as-is via `creatable`'s fallback
  // rather than blank, since nothing in `dishOptions`/`unitOptions` will ever
  // match it.
  const dishValue = dish.foodId != null ? String(dish.foodId) : dish.dish;
  const unitValue = dish.unitId != null ? String(dish.unitId) : dish.unit;

  const handleDishChange = (raw: string) => {
    if (!raw) {
      onUpdate({
        dish: "",
        foodId: null,
        unit: "",
        unitId: null,
        unitLabel: "",
        macros: null,
      });
      return;
    }

    const picked = dishOptions.find((option) => option.value === raw);
    // A new food invalidates whatever unit and nutrition were picked for the
    // last one — its units are a different list, fetched fresh once this
    // lands on `dish.foodId`.
    onUpdate({
      dish: picked?.label ?? raw,
      // Only a row that was picked off the list has an id: `creatable` hands
      // back the typed text itself, which is a name and not a number.
      foodId: picked ? Number(picked.value) : null,
      unit: "",
      unitId: null,
      unitLabel: "",
      macros: null,
    });
  };

  // Read off the units themselves rather than off `unitOptions`, whose labels
  // carry the weight as well: `unit` is the unit's name and nothing else, the
  // way every row that predates the catalog endpoints holds it.
  const handleUnitChange = (raw: string) => {
    const picked = units.options.find((unit) => String(unit.id) === raw);
    onUpdate({
      unit: picked?.name ?? "",
      unitId: raw ? Number(raw) : null,
      // Written out here rather than left for the nutrition call to fill in,
      // so the option's summary reads "Cup (small) - 100gm" from the moment
      // the unit is picked rather than a beat later.
      unitLabel: picked ? unitOptionLabel(picked) : "",
      macros: null,
    });
  };

  const macros = dishMacros(dish);

  return (
    <>
      <td className={optionCell}>
        <CustomDropdown
          size="sm"
          options={dishOptions}
          value={dishValue}
          onChange={handleDishChange}
          placeholder="Search dish"
          searchable
          creatable
          remoteFilter
          loading={foodSearch.loading}
          onSearchChange={foodSearch.setQuery}
          onEndReached={foodSearch.loadMore}
          // The catalog is long but it is not everything, and a dietitian who
          // cooks something it has never heard of should not have to leave the
          // plan to write it down.
          footerAction={{ label: "Custom dish", onSelect: onWriteCustom }}
          // The key to the mark above, pinned like the row below is. Always
          // there rather than only when a marked row happens to be on screen:
          // a key that comes and goes is one the reader has to notice before
          // they can use it, and its absence would itself read as an answer —
          // "you have no dishes of your own" — over a list that has simply
          // been filtered down to the catalog's rows.
          listHeader={
            <>
              <DishMark aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
              <span>= Your dishes</span>
            </>
          }
          aria-label="Dish"
        />
      </td>
      <td className={optionCell}>
        <CustomDropdown
          size="sm"
          options={unitOptions}
          value={unitValue}
          onChange={handleUnitChange}
          creatable
          placeholder={dish.foodId ? "Select unit" : "Pick a dish first"}
          disabled={!dish.foodId || units.loading}
          // Reaching for the unit before there is a food to fill it from is a
          // reasonable thing to do, so it gets an answer rather than a
          // control that simply refuses to respond.
          onDisabledClick={() => {
            if (!dish.foodId) {
              toast.warning(
                "Select a dish first",
                "The unit list is filled by the dish you pick."
              );
            }
          }}
          aria-label="Unit"
        />
      </td>
      <td className={optionCell}>
        <NumberInput
          value={dish.quantity}
          onChange={(quantity) => onUpdate({ quantity })}
          placeholder="Quantity"
          label="Quantity"
        />
      </td>
      {MACRO_COLUMNS.map((macro) => (
        <td key={macro.key} className={optionTextCell}>
          {formatMacro(macros[macro.key])}
        </td>
      ))}
    </>
  );
};

/**
 * A row written out by hand, as it stands once the dialog has been through.
 *
 * Read rather than written, which is the whole difference from a catalog row:
 * a custom dish is eight answers that only make sense together — a container,
 * what one of it comes to, and four figures for one serving of that — so it is
 * written in `CustomDishModal`, where there is room to ask them, and shown
 * here as what it came to. The pencil is the way back into that dialog.
 *
 * It wears a mark down its left edge, so a table of eight rows says which of
 * them the catalog stands behind and which it does not.
 */
const CustomDishCells: React.FC<{
  dish: DishEntry;
  onEdit: () => void;
}> = ({ dish, onEdit }) => {
  const macros = dishMacros(dish);

  return (
    <>
      <td className={optionTextCell}>
        {/* As tall as a row of controls, so a custom row among catalog ones
            does not sit shorter than the rest of the table. */}
        <div className="flex min-h-[34px] items-center gap-2 3xl:min-h-[48px]">
          <span
            aria-hidden="true"
            className="h-[18px] w-[5px] flex-shrink-0 rounded-full bg-brand"
          />
          <span className="min-w-0 flex-1 truncate" title={dish.dish}>
            {dish.dish}
          </span>
          <button
            type="button"
            aria-label={`Edit ${dish.dish || "custom dish"}`}
            title="Edit custom dish"
            onClick={onEdit}
            className="flex-shrink-0 rounded-md p-1 text-ink-muted transition-colors hover:bg-brand-soft hover:text-brand-dark"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
      <td className={optionTextCell}>
        <span className="block truncate" title={unitLabel(dish)}>
          {unitLabel(dish)}
        </span>
      </td>
      <td className={optionTextCell}>{dish.quantity}</td>
      {MACRO_COLUMNS.map((macro) => (
        <td key={macro.key} className={optionTextCell}>
          {formatMacro(macros[macro.key])}
        </td>
      ))}
    </>
  );
};

interface DishRowProps {
  dish: DishEntry;
  /** How many rows the option has — the last empty one cannot be deleted. */
  totalRows: number;
  onUpdate: (patch: Partial<DishEntry>) => void;
  onRemove: () => void;
}

/**
 * One dish in an option — picked off the catalog, or written out by hand.
 * Which of the two it is decides the cells in the middle; the bin at the end
 * of the row, and the question it asks, are the same either way.
 *
 * Deleting asks first, and asks in place: the row is replaced by the question
 * rather than a dialog opening over the plan. A dialog for a row that is one
 * dropdown wide is heavier than the thing it is protecting, and it takes the
 * eye off the table to answer.
 */
const DishRow: React.FC<DishRowProps> = ({
  dish,
  totalRows,
  onUpdate,
  onRemove,
}) => {
  const [confirming, setConfirming] = useState(false);

  /**
   * The row the custom dish dialog is open on, or nothing while it is shut.
   *
   * A row being written from scratch opens on a blank entry carrying the name
   * that was typed into the Dish field — a filter that matched nothing is
   * nearly always the name being looked for. A custom row being modified opens
   * on itself. Either way what the dialog answers with is a patch onto the row
   * this component already holds, so the seed is only ever what fills the
   * boxes in.
   */
  const [customSeed, setCustomSeed] = useState<DishEntry | null>(null);

  // The one row of an untouched option: there is nothing in it to delete, and
  // taking it out would only put an identical one back. A custom row is never
  // that row — nothing reaches one until the dialog has been filled in.
  const isRowEmpty =
    totalRows === 1 && !dish.dish && !dish.unit && !dish.quantity;

  if (confirming) {
    return (
      <tr>
        <td className={`${optionCell} pr-0 3xl:pr-0`} colSpan={OPTION_COLUMN_COUNT}>
          {/* The question floats over the space the row's controls held rather
              than standing in it, so it can be the size a question wants to be
              without the rows below it moving to make room.

              What holds that space is an invisible copy of a dropdown's own
              trigger — the same classes the real ones wear, so it is exactly
              as tall as what it stands in for, and stays that way if the size
              of the control ever changes. */}
          <div className="relative">
            <div
              aria-hidden="true"
              className={`invisible ${dropdownTriggerClasses(
                false,
                false,
                "sm"
              )}`}
            >
              {/* The trigger's two parts, which are what give it its height:
                  a line of its own type, and the chevron beside it. */}
              <span>&nbsp;</span>
              <span className="h-4 w-4 flex-shrink-0" />
            </div>

            <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-between gap-4 rounded-md border-[0.5px] border-[#FFCCC9] bg-[#FDECEC] px-3 py-1">
              <span className="min-w-0 truncate text-[14px] text-ink">
                Delete{" "}
                {dish.dish ? (
                  <span className="font-medium">&ldquo;{dish.dish}&rdquo;</span>
                ) : (
                  "this row"
                )}
                ?
              </span>
              <div className="flex flex-shrink-0 items-center gap-2">
                <Button
                  variant="danger"
                  size="xs"
                  onClick={onRemove}
                  className="whitespace-nowrap"
                >
                  Confirm delete
                </Button>
                <button
                  type="button"
                  aria-label="Keep this row"
                  onClick={() => setConfirming(false)}
                  className="rounded-md border-[0.5px] border-ink p-1 text-ink-muted transition-colors hover:text-ink"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr>
        {dish.custom ? (
          <CustomDishCells dish={dish} onEdit={() => setCustomSeed(dish)} />
        ) : (
          <CatalogDishCells
            dish={dish}
            onUpdate={onUpdate}
            onWriteCustom={(typed) =>
              setCustomSeed({ ...createDishEntry(), dish: typed })
            }
          />
        )}
        <td className={`${optionCell} pr-0 3xl:pr-0`}>
          <button
            type="button"
            aria-label="Remove dish"
            title={isRowEmpty ? "Nothing to delete" : "Delete row"}
            disabled={isRowEmpty}
            onClick={() => setConfirming(true)}
            // Grey at rest and red under the pointer — the bin's two states, in
            // the order the row means them. A row with nothing in it stays grey
            // and pales further, so it never offers the red.
            className={`rounded-md p-1.5 transition-colors ${
              isRowEmpty
                ? "cursor-not-allowed text-[#D0D7DD]"
                : "text-[#8B8D98] hover:bg-[#FDECEC] hover:text-[#FF3B30]"
            }`}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </td>
      </tr>

      {/* A sibling of the row rather than a cell in it: the dialog portals
          itself to the body, so it draws nothing here — and a `<tr>` is no
          place to nest one anyway. */}
      {customSeed && (
        <CustomDishModal
          dish={customSeed}
          onClose={() => setCustomSeed(null)}
          onSave={(patch) => {
            onUpdate(patch);
            setCustomSeed(null);
          }}
        />
      )}
    </>
  );
};

export interface MealOptionCardProps {
  option: MealOption;
  /** Zero-based. The delete is named after the option it belongs to. */
  index: number;
  /**
   * What this entry is called on a day-based plan — "day". Passed straight to
   * the delete, which is the only thing here that names it. See
   * `OptionActions`.
   */
  unitName?: string;
  onChange: (option: MealOption) => void;
  onRemove: () => void;
  onSave: () => void;
  /** Set for the meal's only option while nothing has been typed into it. */
  removeDisabled?: boolean;
  /**
   * Whether the workspace may write this plan at all — see
   * `useSubscriptionGate`. A card is not normally reachable while it is off,
   * since Modify is off too; the exception is the blank option a meal opens
   * with, which opens as a form on its own. So Save carries the reason rather
   * than storing something the server would refuse anyway. The table above it
   * is left alone: what is typed into a card goes nowhere until Save.
   */
  gate?: GateProps;
}

/**
 * An option as it is written: the same table `MealOptionSummary` reads, with a
 * control in every cell.
 *
 * The macros are read off the catalog rather than typed, so a row shows
 * nothing until it has a dish, a unit and a quantity — see `dishMacros`. Save
 * waits on the same thing: there is nothing to store until one row is whole,
 * and nothing to read back if it were stored.
 */
const MealOptionCard: React.FC<MealOptionCardProps> = ({
  option,
  index,
  unitName,
  onChange,
  onRemove,
  onSave,
  removeDisabled = false,
  gate = { disabled: false },
}) => {
  const instructions = option.instructions ?? "";

  // Whether the instructions box is showing. Open from the start on an option
  // that has some, since hiding text that is there would look like losing it.
  // The same pair `SupplementCard` keeps for its note.
  const [instructing, setInstructing] = useState(() => !!instructions.trim());

  const updateDish = (dishId: string, patch: Partial<DishEntry>) => {
    onChange({
      ...option,
      dishes: option.dishes.map((dish) =>
        dish.id === dishId ? { ...dish, ...patch } : dish
      ),
    });
  };

  const removeDish = (dishId: string) => {
    const remaining = option.dishes.filter((dish) => dish.id !== dishId);
    onChange({
      ...option,
      // The table always keeps a row to type into: emptying it out leaves a
      // blank row rather than a header with nothing under it.
      dishes: remaining.length > 0 ? remaining : [createDishEntry()],
    });
  };

  const addDish = () => {
    onChange({ ...option, dishes: [...option.dishes, createDishEntry()] });
  };

  return (
    // The same card the option is read on: the white controls appearing in it
    // are difference enough, and a card that changed colour under Modify would
    // read as the option having been replaced rather than opened.
    <div className={optionCardClasses}>
      {/* The dropdown panels are portalled to the body, so this scroller can
          never clip an open list. */}
      <div className="overflow-x-auto custom-scrollbar-overlay px-4 pt-4">
        <table className={optionTableClasses}>
          <OptionColgroup />
          <thead>
            <tr>
              <th scope="col" className={optionHeadCell}>
                Name
              </th>
              <th scope="col" className={optionHeadCell}>
                Unit
              </th>
              <th scope="col" className={optionHeadCell}>
                Quantity
              </th>
              {MACRO_COLUMNS.map((macro) => (
                <th key={macro.key} scope="col" className={optionHeadCell}>
                  <MacroHeading macro={macro.key} label={macro.label} />
                </th>
              ))}
              <th scope="col" className={`${optionHeadCell} pr-0 3xl:pr-0`}>
                <span className="sr-only">Remove dish</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {option.dishes.map((dish) => (
              <DishRow
                key={dish.id}
                dish={dish}
                totalRows={option.dishes.length}
                onUpdate={(patch) => updateDish(dish.id, patch)}
                onRemove={() => removeDish(dish.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 pb-4">
        {/* Sized off the rows above it, so it stays the same square the
            controls are tall — a size down until `3xl`, like they are. */}
        <button
          type="button"
          onClick={addDish}
          aria-label="Add dish"
          className="inline-flex h-7 w-7 3xl:h-8 3xl:w-8 items-center justify-center rounded-[6px] border border-[#5BD3E1] bg-white text-brand transition-colors hover:bg-brand-soft"
        >
          <Plus className="h-3.5 w-3.5 3xl:h-4 3xl:w-4" />
        </button>

        {/* Anything the table above does not say. Behind a toggle because
            most options need none, and a box open on every card would read
            as a question every card has to answer. */}
        <div className="mt-4">
          {/* The opener on the left and the way out at the far right, level
              with it — so the row reads as the whole of the section rather
              than as one control with something stuck beside it. */}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              // Opens only. Closing is the × at the other end, which does more
              // than hide the box — so one control cannot mean both.
              onClick={() => setInstructing(true)}
              aria-expanded={instructing}
              aria-controls={`${option.id}-instructions`}
              className="inline-flex items-center gap-1.5 rounded text-[14px] text-ink transition-colors hover:text-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              Instructions
            </button>

            {instructing && (
              <button
                type="button"
                // Closing takes the text with it, which is what keeps the two
                // states honest: a box that is open is an option with
                // instructions on it, and a closed one is an option without.
                // Text left behind a shut box would still be read back on the
                // saved card, which is the one thing nobody would expect.
                //
                // Named for what it does rather than for the glyph, so it is
                // not pressed as a plain dismiss.
                onClick={() => {
                  onChange({ ...option, instructions: "" });
                  setInstructing(false);
                }}
                aria-label={`Remove instructions from option ${index + 1}`}
                className="rounded p-1 text-ink-subtle transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            )}
          </div>

          {instructing && (
            <textarea
              id={`${option.id}-instructions`}
              value={instructions}
              onChange={(event) =>
                onChange({ ...option, instructions: event.target.value })
              }
              placeholder="Maintain a 30-minute gap before or after meals."
              rows={3}
              maxLength={500}
              aria-label={`Instructions for option ${index + 1}`}
              autoFocus
              className="mt-3 w-full resize-y rounded-[6px] border border-line bg-white px-3.5 py-2.5 text-[14px] text-ink placeholder:text-ink-subtle focus:border-brand focus:outline-none"
            />
          )}
        </div>
      </div>

      {/* Inset to the card's own padding, so it separates the rows from the
          actions without reaching the border on either side. */}
      <div className={`mx-4 border-t-[0.5px] ${optionRuleClasses}`} />

      <div className="px-4 py-3">
        <OptionActions
          index={index}
          unitName={unitName}
          onRemove={onRemove}
          removeDisabled={removeDisabled}
          gate={gate}
        >
          <Button
            size="sm"
            onClick={onSave}
            // Nothing to store until a row is whole — and a plan saved from a
            // row half filled in is a plan that reads as empty. That one stays
            // plainly disabled: the empty table above it is the reason, and it
            // is already on screen. A workspace with no plan behind it gets the
            // reason it cannot see instead.
            {...(option.dishes.some(isDishComplete)
              ? gate
              : { disabled: true })}
          >
            Save
          </Button>
        </OptionActions>
      </div>
    </div>
  );
};

export default MealOptionCard;
